<template>
  <HspotsContainer
    ref="HspotsContainer"
    :scrollerWatchActionId="scrollerWatchActionId"
    :minimized="minimized"
    :hasMinimized="hasMinimized"
    :innerCssClass="innerCssClass"
    :openToTop="openToTop"
    :isMinimizedVertical="isMinimizedVertical"
    :namespace
  >
    <template #before="{ minimized }">
      <div
        v-if="hasShortcuts || hasGameformat"
        class="d-flex"
        style="gap: 4px; align-items: var(--hs-align-items)"
      >
        <ShortcutBtns
          v-if="hasShortcuts"
          class="z-index_4"
          :canAISolve="canAISolve"
          :namespace
          :hiddenBtns="hiddenShortcutBtns"
          :hasExpand="!mobile"
          :hasKeys="!mobile"
          :disableReplay="player_animation.waiting"
          :horizontal="mobile && shortcutBtnsHorizontal"
          :minimized="minimized"
          @click="$emit('click')"
          @reset="shortcutsResetHistory"
          @click.stop
        />
        <GameFormatSelector
          v-if="hasGameformat"
          :solElite="solElite"
          class="z-index_3"
          :minimized="minimized"
          :namespace
          style=""
          @click.stop
        >
        </GameFormatSelector>
      </div>
    </template>
    <template #content="v">
      <EventCard
        v-for="(item, idx) in history_spots_complete"
        :ref="`item_${item.id}`"
        :key="`${item.id}_${idx}`"
        :data-tst="hs_tstId(item)"
        :namespace
        :item="item"
        :mobile="mobile"
        :minimized="v.minimized"
        :bettingInfo
        :showBetsIn
        :showEvIn
        :openToTop
        :hasNodeEdit
        :hasNodeMenu
        :hasDisabledZeroFreqActions
        :requiredToGenerateReport
        :isCustomReportsPending
        :isMinimizedVertical="isMinimizedVertical"
        :skeleton="item.isSkeleton"
        :bot="item.is_bot"
        :active="item.active"
        :loading="loader ? item.id === loader.history_spot : false"
        :botWaiting="player_animation.waiting"
        :class="[
          {
            'no-events':
              (item.type === 'showdown' && !clickableGG && !hasGGSaveSpot) ||
              (item.type === 'board' && !clickableBoard) ||
              (loader ? item.id === loader.history_spot : false) ||
              item.disabled ||
              item.soon,
          },
          {
            hspotcrd_disabled: hasGGSaveSpot && item.type === 'showdown',
          },
        ]"
        @click:nodeedit="handleNodeEditClick(item)"
        @click:nodelock="handleNodeLockClick(item)"
        @click:nodecompare="handleNodeCompareClick(item)"
        @click="handleClick(item)"
        @actionClick="
          action => {
            handleActionClick(item, action, idx)
          }
        "
      >
        <template
          v-if="loader ? !loader.extra && item.id === loader.history_spot : false"
          #loading
        >
          <LoaderDots
            v-if="loader.dots"
            data-tst="event_loading"
            class="absolute-center"
            style="color: var(--clr-opponent)"
          />
          <LoaderSpinner
            v-if="loader.spinner"
            :style="`--size:${v.minimized ? 1.5 : 2}em`"
            class="absolute-center"
          />
        </template>
        <template
          v-else-if="item.type === 'showdown'"
          #message
        >
          <slot name="custom_showdown_message">
            <div class="f-center f-column clr-grey w-100 weight_700">
              {{ $t('common.gg_short') }}
              <div
                v-if="hasGGSaveSpot && !v.minimized"
                class="gw_btn mt-2 csst text-nowrap"
                @click="
                  setDialogs(`saved-spots-dialog${namespace ? `_namespace-${namespace}` : ''}`)
                "
              >
                <i class="mdi mdi-content-save text-large" />
                <div
                  class="csst_content shrt_btn_tooltip"
                  style="transform: translate(0, calc(-100% - 0.5rem))"
                  v-html="$t('btn.save')"
                ></div>
              </div>
            </div>
          </slot>
        </template>
        <template
          v-else-if="item.type === 'player'"
          #title
        >
          <div :class="v.minimized ? 'd-flex' : 'f-column w-100'">
            <div class="d-flex w-100 f-center">
              {{ item.position }}

              <div
                v-if="item.is_node_locked"
                class="mdi mdi-lock"
                style="margin: 0 0 0 0.125rem; font-size: 0.7rem"
              ></div>

              <div class="ml-auto pl-2">{{ getStack(item) }}</div>
            </div>

            <GwTooltipNew
              v-if="item.bounty"
              placement="right"
              :disabled="showBounty === 'all'"
            >
              <template #opener="{ listeners }">
                <div
                  class="ml-auto pl-2 d-flex"
                  style="gap: 0.25rem; text-transform: none"
                  v-on="listeners"
                  @click="toggleBounty"
                >
                  <div
                    class="mdi clr-grey mdi-target f-center my-auto"
                    style="font-size: 1.125em; height: 1em"
                  ></div>
                  {{ getBountyDisplay(item, showBounty) }}
                </div>
              </template>
              <template #tooltip>
                <span class="weight_700">
                  {{ showBounty === 'usd' ? `~${item.bounty.bb}bb` : `$${item.bounty.usd}` }}
                </span>
              </template>
            </GwTooltipNew>
          </div>
        </template>
        <template
          v-else-if="item.type === 'board'"
          #title
        >
          {{ item.street }}
          <div
            v-show="item.start_pot"
            class="hspotcrd_title_stack"
          >
            {{ item.start_pot }}
          </div>
        </template>

        <template
          v-if="item.type === 'board' && !item.is_filter"
          #cards
        >
          <CardRow
            class="position-relative mx-auto mb-auto"
            :cards="visibleBoard[item.street]"
            :size="v.minimized ? 'smallest' : 'large'"
            :hasAnimation="false"
          />
          <BtnPrefillAi
            v-if="!v.minimized && item.has_ai_prefill && gameformat.subscription_tier"
            style="margin: 0.5rem -0.5rem 0.25rem auto"
            tstPrefix="hsboard"
            @click:prefill="$emit('click:prefill')"
          >
            <div
              id="btn_prefill"
              class="gw_btn gw_btn_small gw_btn_text clr-grey clr-hover"
              style="--padding: 0 0.5rem"
            >
              <i class="text-medium mdi mdi-auto-fix mr-1"></i>
              <span class="text-medium weight_500">
                {{ $t(`solfigr.ai_solve`) }}
              </span>
            </div>
          </BtnPrefillAi>
        </template>
        <template
          v-else-if="item.type === 'board' && item.is_filter"
          #filter_items
        >
          <div
            class="w-100 clr-grey text-small"
            :class="v.minimized ? '' : 'mb-auto'"
          >
            <CardRow
              :cards="visibleBoard[item.street]"
              :size="
                board[item.street]
                  ? v.minimized
                    ? 'smallest'
                    : 'large'
                  : v.minimized
                    ? 'smallest'
                    : 'small'
              "
              :hasAnimation="false"
            >
            </CardRow>
            <div
              v-if="!v.minimized"
              class="history-events-pencil-icon pa-1 mdi icon_btn mdi-filter text-normal clr-white csst csst_nm"
              @mouseenter="e => fl_positionTooltip(e, { placement: 'top' })"
            >
              <div
                class="csst_content text-nowrap"
                style="position: fixed"
                v-html="$t('tooltips.choose_filter')"
              />
            </div>
            <div
              v-if="!board[item.street] && !v.minimized"
              class="d-flex mt-2 mb-auto"
            >
              <div
                v-if="!getRules(item).length"
                class="text-transform-none filter-rule ma-0"
                data-tst="no_filter_rules"
              >
                {{ $t('common.any') }}
              </div>
              <div
                v-else
                class="f-column"
              >
                <div
                  v-for="(rule, ruleIndex) in getRules(item)"
                  :key="rule.titleKey || rule"
                  class="text-transform-none filter-rule ma-0"
                  :data-tst="`filter_rule_${ruleIndex}`"
                >
                  <div v-if="rule.titleKey && rule.values.length && ruleIndex < 3">
                    {{ $t(`practice.filter.shorthand.${rule.titleKey}`) }}:
                    {{ rule.values.join(',') }}
                  </div>
                  <div v-else-if="ruleIndex < 3">
                    {{ $te(`practice.filter.${rule}`) ? $t(`practice.filter.${rule}`) : rule }}
                  </div>
                  <div v-else-if="ruleIndex === 3">
                    <span :data-tst="`filter_more-dots`"> *** </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </template>

        <!-- eslint-disable -->
        <template
          v-else-if="item.type === 'solution_end'"
          #message
        >
          <!-- eslint-enable -->
          <div
            class="f-center f-column w-100 text-medium my-2"
            style="gap: 1rem"
          >
            <slot name="solution_end_message">
              <div
                v-if="!v.minimized"
                class="text-center weight_600 clr-greys"
                style="max-width: 12rem"
                v-html="$t(`messages.solution_no_postfop_abbr`)"
              ></div>
            </slot>

            <BtnPrefillAi
              v-if="item.has_ai_prefill && gameformat.subscription_tier"
              @click:prefill="$emit('click:prefill')"
            >
              <div class="gw_btn gw_btn_primary weight_500 clr-white">
                <i class="mdi mdi-auto-fix mr-1"></i>
                {{ $t(`solfigr.solve_postflop_ai`) }}
              </div>
            </BtnPrefillAi>
            <router-link
              v-else-if="item.has_ai_prefill && !gameformat.subscription_tier"
              class="gw_btn gw_btn_primary weight_500 clr-white"
              :to="{
                name: 'SubscriptionOverview',
                query: { gm_type: 'elite', gm_type: gameformat.gm_type },
              }"
            >
              <i class="mdi mdi-crown mr-1"></i>
              {{ $t('btn.upgrade_to_ai_solving') }}
            </router-link>
          </div>
        </template>
      </EventCard>
      <EventCard
        v-if="loader ? loader.extra : false"
        :namespace
        :item="{ is_bot: loader.dots }"
        class="no-events"
        :mobile="mobile"
        :isMinimizedVertical="isMinimizedVertical"
        :minimized="v.minimized"
        :totalsItems
        :isCustomReportsPending
        :active="false"
        :loading="true"
        :botWaiting="loader.dots"
      >
        <template #loading>
          <LoaderDots
            v-if="loader.dots"
            class="absolute-centers"
            style="color: var(--clr-opponent)"
          />
          <LoaderSpinner
            v-if="loader.spinner"
            class="absolute-center"
            :style="`--size:${v.minimized ? 1.5 : 2}em`"
          />
        </template>
      </EventCard>
    </template>
  </HspotsContainer>
</template>

<script>
import { nextTick } from 'vue'
import LoaderSpinner from '@/components/Loaders/LoaderSpinner'
import LoaderDots from '@/components/Loaders/LoaderDots'
import BtnPrefillAi from '@/components/SpecialBtns/BtnPrefillAi'
import {
  setHsPresetSelectedActions,
  setHsSelectedAction,
  getPokerPositions,
  getCompleteHistorySpots,
  getAfterStreet,
  getFlopShowdownNode,
  getSpotSelectedAction,
} from '@/store/gamestate/gamestate'
import { getGameformatQuery, getGameformatGroup } from '@/store/gameformat/gameformat'
import { getCardsByStreet } from '@/helpers/cards'

import GameFormatSelector from '@/components/GameFormat/GameFormatSelector'
import ShortcutBtns from '@/components/Dialogs/PreflopShortcutDialog/ShortcutBtns'
import CardRow from '@/components/CardRow/CardRow'

import format_mix from '@/mixins/format_mix'
import float_mix from '@/mixins/float_mix'
import GwTooltipNew from '@/components/lib/GwTooltip/GwTooltipNew.vue'
import { SNK } from '@/helpers/helpers'
import HspotsContainer from './HspotsContainer'
import EventCard from './EventCard'

// TODO handle - 400 backend will ad this
// https://app.gtowizard.com/solutions?gametype=Cash6m500zBasic&depth=100&history_spot=9&preflop_actions=R2.3-R6.8-F-F-F-F-F-F-RAI-F

// ok
// https://app.gtowizard.com/solutions?gametype=MTTGeneral&depth=40.125&history_spot=9&preflop_actions=R2.3-R6.8-F-F-F-F-F-F-RAI-F

export default {
  name: 'PokerHistoryEvents',
  components: {
    GwTooltipNew,
    EventCard,
    HspotsContainer,
    ShortcutBtns,
    GameFormatSelector,
    CardRow,
    LoaderSpinner,
    LoaderDots,
    BtnPrefillAi,
  },
  mixins: [format_mix, float_mix],
  // TR-TODO move what need not to be computed to static action vars
  props: {
    namespace: { String, required: true },
    canAISolve: { required: true, type: Boolean },
    hasTurnPreload: Boolean,
    noNextActionsWithoutBoard: Boolean,
    minimized: Boolean,
    hasMinimized: { type: Boolean, default: true },
    isMinimizedVertical: Boolean,
    shortcutBtnsHorizontal: Boolean,
    hasGGSaveSpot: Boolean,
    bettingInfo: Object,

    useTreePreview: Boolean,
    useTreePreviewReports: Boolean,
    customTreeId: String,

    solElite: Object,
    hasEmptyBoard: Boolean,
    hasDisabledZeroFreqActions: Boolean,

    hasGameformat: Boolean,

    clickableGG: Boolean,
    clickableBoard: { type: Boolean, default: true },
    hasNodeEdit: Boolean,
    hasNodeMenu: Boolean,

    hasFilter: Boolean,
    hasTmpBoard: Boolean,
    hasCorrectness: Boolean,

    customLoadingEvents: Boolean,
    loadingSkeleton: Boolean,
    skeletonTiles: { default: 0 },

    actionEmitOnly: Boolean,
    hasInitialLoad: Boolean,

    hasWatcherEnabled: { type: Boolean, default: true },
    customLoading: { default: () => null },

    showBoardWhenLoading: Boolean,

    hasShortcuts: { type: Boolean, default: true },
    hasActiveSpot: { type: Boolean, default: true },
    requiredToGenerateReport: { type: Boolean, default: false },
    openToTop: Boolean,
    innerCssClass: String,
    hiddenShortcutBtns: { default: () => [] },
    dialog: { type: String, default: () => '' },

    hasFlopPrefillBtn: Boolean,
    hasFlopSolutionEndNode: Boolean,
    clickableFlopSolutionEndNode: Boolean,
    isCustomReportsPending: Boolean,

    stopOnEndOfStreet: { default: '' },
    ggStreet: { default: '' },
    totalsItems: Array,

    routerParams: {
      default: () => ({
        push: true,
        replace: false,
        saveLocally: true,
      }),
    },
  },
  emits: [
    'click',
    'click:prefill',
    'click:nodeedit',
    'click:nodelock',
    'click:action',
    'click:nodecompare',
  ],
  data() {
    return {
      localQuery: {},
      watcherEnabled: false,
      l_str_query_history: '',
      l_str_query_history_spot: '',
      heightDiff: 0,
      showBounty: localStorage.getItem('showBounty') || 'usd',
      // hoverIdx: 0,
    }
  },

  created() {
    this.l_str_query_history = this.str_query_history
    this.l_str_query_history_spot = this.query_history_spot

    if (!this.hasInitialLoad) return

    this.getHistory()
  },
  methods: {
    handleNodeEditClick(item) {
      this.handleClick(item)
      this.$emit('click:nodeedit', item)
    },
    handleNodeLockClick(item) {
      this.handleClick(item)
      this.$emit('click:nodelock', item)
    },
    handleNodeCompareClick(item) {
      this.handleClick(item)
      this.$emit('click:nodecompare', item)
    },
    getStack(item) {
      return item?.player ? this.numFixedUnit(item.player.current_stack, 'bb') : ''
    },
    getHistory() {
      // Used in Trainer and in Create Custom Solution dialog
      if (this.useTreePreview) {
        this.$store.dispatch(SNK('slfrm_getTreePreview', this.namespace), {
          fromStart: true,
          filterToPrevSpot: false,
          customTreeId: this.customTreeId,
        })
        return
      }

      if (this.solElite.want_use) {
        // Used only in custom Reports
        if (this.useTreePreviewReports) {
          if (this.solElite.custom_tree_loaded) {
            this.$store.dispatch(SNK('rpfrm_getTreePreview', this.namespace), {
              customTreeId: this.customTreeId,
              enable_hsWatcher: true,
            })
            return
          }
          // get the tree and then call for gamestate
          this.$store.dispatch(SNK('sol_reportsInitialize', this.namespace))
          return
        }

        // Used only in Custom Solutions
        if (this.solElite.custom_solution_loaded) {
          this.$store.dispatch('api_poker_history', {
            namespace: this.namespace,
            enable_hsWatcher: true,
            cussol_id: this.solElite.custom_solution_id,
          })
          return
        }
        // get the sol and then call for gamestate
        this.$store.dispatch(SNK('sol_studyInitialize', this.namespace))
        return
      }

      // solution library solutions
      this.$store.dispatch('api_poker_history', {
        namespace: this.namespace,
        enable_hsWatcher: true,
      })
    },
    getStartStreetPot(street) {
      const res = this.historyEvents.find(e => e.type === 'player' && e.street === street)
      return res ? `${res.table_pot} BB` : ''
    },
    openDialog(street) {
      const tmpNamespace = 'tmp/primary'
      const cpParams = {
        from_namespace: this.namespace,
        to_namespace: tmpNamespace,
        queryGroup: 'board-filter',
      }
      this.$store.dispatch('poker_copy_module', cpParams)
      let { dialog } = this
      dialog = `${dialog}_street-${street}${
        this.namespace ? `_namespace-${this.namespace}` : ''
      }_tmpNamespace-${tmpNamespace}`
      this.setDialogs(dialog)
    },
    setDialogs(dialog) {
      this.$store.dispatch('router_set_dialogs', dialog)
    },
    handleClick(item) {
      if (!this.hasActiveSpot) return
      if (item.type === 'showdown' && !this.clickableGG) return

      this.$store.dispatch(SNK('gamestate_reset_next', this.namespace))

      this.$emit('click', item)
      if (item.type === 'board' && this.dialog) {
        this.openDialog(item.street)
        return
      }

      if (!item.active) {
        this.toggleWatcher(false)
        let { history_spots } = this
        if (item.preset_action_code) {
          history_spots = setHsPresetSelectedActions({ history_spots, spot: item })
        }
        this.setHistory({
          history_spots,
          spot_id: item.id,
        })

        setTimeout(() => {
          this.toggleWatcher(true)
        })
      }
    },
    handleActionClick(item, action) {
      this.$emit('click', item)

      this.toggleWatcher(false)

      if (this.actionEmitOnly) {
        this.$emit('click:action', { item, action })
        return
      }

      // doubleclick on action leadigg to GG
      if (action.outcome === 'showdown' && action.is_selected && !this.clickableGG) {
        this.handleClick(item)
        return
      }

      if (!action.is_selected) {
        let hSpots = this.history_spots

        hSpots = hSpots.filter(e => e.id <= item.id)

        if (item.preset_action_code) {
          hSpots = setHsPresetSelectedActions({ history_spots: hSpots, spot: item })
        }
        hSpots = setHsSelectedAction({ history_spots: hSpots, spot: item, action, getNext: true })

        if (this.$route.name === 'StudySolutions') {
          let street_board = this.$store.getters[SNK('street_board', this.namespace)](item.street)
          street_board = street_board.filter(c => !c.includes('E')).join('')
          this.setHistory({
            history_spots: hSpots,
            spot_id: item.id,
            query: {
              board: street_board,
            },
          })
        } else {
          this.setHistory({
            history_spots: hSpots,
            spot_id: item.id,
          })
        }

        if (this.hasFlopSolutionEndNode && action.is_fe_solution_end && item.street === 'preflop') {
          const { history_spots } = getFlopShowdownNode({
            action,
            currentSpot: item,
            history_spots: hSpots,
          })
          this.setHistory({
            history_spots,
            spot_id: this.clickableFlopSolutionEndNode ? item.id + 1 : item.id,
          })
          return
        }

        if (action.outcome === 'board') {
          if (this.stopOnEndOfStreet && this.stopOnEndOfStreet === item.street) {
            return
          }
          if (this.hasTurnPreload && item.street === 'flop') {
            if (this.board.flop) {
              this.$store.dispatch('api_poker_turn_preload', { namespace: this.namespace })
            }
          }
          if (this.noNextActionsWithoutBoard) {
            const nextStreet = getAfterStreet({ street: item.street })
            this.openDialog(nextStreet)
            return
          }
        }

        if (this.customTreeId) {
          if (this.useTreePreview) {
            this.$store.dispatch(SNK('slfrm_getTreePreview', this.namespace), {
              fromStart: false,
              filterToPrevSpot: false,
              customTreeId: this.customTreeId,
            })
            return
          }
          if (this.useTreePreviewReports) {
            this.$store.dispatch(SNK('rpfrm_getTreePreview', this.namespace), {
              fromStart: false,
              filterToPrevSpot: false,
              customTreeId: this.customTreeId,
              enable_hsWatcher: true,
            })
            return
          }
        }

        this.$store.dispatch(SNK('gamestate_get_next_actions', this.namespace), {
          namespace: this.namespace,
          routerParams: this.routerParams,
          enable_hsWatcher: true,
        })
        return
      }

      if (action.outcome === 'board') {
        if (item.street === this.stopOnEndOfStreet) {
          return
        }
        if (this.noNextActionsWithoutBoard) {
          const nextStreet = getAfterStreet({ street: item.street })
          this.openDialog(nextStreet)
          return
        }
      }

      this.setHistory({
        history_spots: this.history_spots,
        spot_id: item.id + 1,
      })

      setTimeout(() => {
        this.toggleWatcher(true)
      })
    },
    setHistory(params) {
      this.l_str_query_history_spot = params.spot_id.toString()
      this.$store.dispatch(SNK('gamestate_set_history', this.namespace), {
        ...params,
        ...this.routerParams,
      })
    },
    toggleWatcher(bool) {
      if (this.hasWatcherEnabled) {
        this.watcherEnabled = bool
      }
    },
    getRules(item) {
      const filter = this.filterParams
      const activeSections = Object.keys(filter).filter(key => key.includes(item.street))
      let selectedRules = activeSections
        .map(key => {
          if (key === 'flop_high_card' || key === 'flop_mid_card' || key === 'flop_low_card') {
            return {
              titleKey: key,
              values: filter[key],
            }
          }
          return filter[key]
        })
        .flat()
        .filter((i, idx) => idx < 5)
      selectedRules = selectedRules.filter(i => (i.values ? i.values.length : true))
      return selectedRules
    },
    getTilesByPlayers() {
      let res = getPokerPositions({ gameformat: this.gameformat })
      res = (res?.length || 6) - 2
      return res
    },
    async handleUrlParamsChange() {
      // console.log({
      //   l_str_query_history: this.l_str_query_history,
      //   str_query_history: this.str_query_history,
      //   l_str_query_history_spot: this.l_str_query_history_spot,
      //   query_history_spot: this.query_history_spot,
      //   replay: this.$store.getters.panel_replay,
      //   hasWatcherEnabled: this.hasWatcherEnabled,
      //   watcherEnabled: this.watcherEnabled,
      //   strMatch: this.l_str_query_history !== this.str_query_history,
      // })

      if (this.$store.getters.panel_replay) return
      if (this.hasWatcherEnabled && this.watcherEnabled) {
        if (this.l_str_query_history !== this.str_query_history) {
          this.l_str_query_history = this.str_query_history
          this.l_str_query_history_spot = this.query_history_spot

          const q = this.$route.query
          const gametype = q.gametype || ''
          const group = getGameformatGroup(gametype, 'Cash')
          if (!group) {
            return
          }
          if (gametype && !this.$store.getters.gameformats_groupsSet[group]) {
            await this.$store.dispatch('get_gameformat_groups', [group])
          }

          this.$store.dispatch('poker_gamestate_history_cancel_pending_requests')

          const { gameformats } = this.$store.getters
          const { query: gameformatQuery } = getGameformatQuery({
            gameformats,
            lsKey: this.namespace || this.$route.name,
            localOnly: false,
          })

          const query = {
            soltab: q.soltab || 'strategy',
            preflop_actions: q.preflop_actions || '',
            flop_actions: q.flop_actions || '',
            turn_actions: q.turn_actions || '',
            river_actions: q.river_actions || '',
            gametype: q.gametype || '',
            depth: q.depth || '',
            stacks: q.stacks || '',
            cussol_id: q.cussol_id || '',
            custree_id: q.custree_id || '',
            solution_type: q.solution_type || 'gwiz',
            ...gameformatQuery,
            history_spot: this.query_history_spot,
          }
          this.$store.dispatch(SNK('gamestate_reset_history', this.namespace), { query })

          setTimeout(() => {
            this.getHistory()
            this.$store.dispatch('poker_copy_module', {
              from_namespace: '',
              to_namespace: '',
              queryGroup: 'router-to-gmf-selektor',
            })
          }, 0)

          return
        }

        const { history_spot } = this.$store.getters
        if (history_spot?.id?.toString() === this.query_history_spot) {
          this.l_str_query_history_spot = this.query_history_spot
          return
        }

        if (this.l_str_query_history_spot !== this.query_history_spot) {
          this.setHistory({
            history_spots: this.history_spots,
            spot_id: Number(this.query_history_spot),
          })
          // console.log('hsSpots updating hSpot')
        }
      } else {
        this.l_str_query_history = this.str_query_history
        this.l_str_query_history_spot = this.query_history_spot
      }
    },
    hs_tstId(item) {
      // hs_flop_QsTs7h
      if (item.type === 'board') {
        return `hs_${item.id}${item.board ? `_${item.board}` : ''}${item.active ? '_active' : ''}`
      }
      // hs_0_preflop_BTN || hs_0_preflop_BTN_active
      if (item.type === 'player') {
        return `hs_${item.id}_${item.street}${item.position ? `_${item.position}` : ''}${
          item.active ? '_active' : ''
        }`
      }
      // hs_river_showdown
      return `hs_${item.street}_${item.type}`
    },
    shortcutsResetHistory() {
      this.watcherEnabled = false
      this.$store.dispatch(`${this.namespace ? `${this.namespace}/` : ''}gamestate_reset_history`, {
        namespace: this.namespace,
      })
      this.getHistory()
    },
    getBountyDisplay(item, showBounty) {
      if (!item || !item.bounty) return ''

      switch (showBounty) {
        case 'all':
          return `$${item.bounty.usd} (~${item.bounty.bb}bb)`
        case 'usd':
          return `$${item.bounty.usd}`
        default:
          return `~${item.bounty.bb}bb`
      }
    },
    toggleBounty() {
      const order = ['usd', 'bb', 'all']
      const currentIndex = order.indexOf(this.showBounty)
      const nextValue = order[(currentIndex + 1) % order.length]
      this.showBounty = nextValue
      localStorage.setItem('showBounty', nextValue)
    },
  },
  computed: {
    query_history_spot() {
      return this.$route.query.history_spot || ''
    },
    query_history() {
      const q = this.$route.query
      const res = [
        q.preflop_actions || '',
        q.flop_actions || '',
        q.turn_actions || '',
        q.river_actions || '',
        q.gametype || '',
        q.depth || '',
        q.stacks || '',
        q.cussol_id || '',
        q.custree_id || '',
        q.solution_type || 'gwiz',
      ]
      // console.log('query_history', res)
      return res
    },
    str_query_history() {
      return JSON.stringify(this.query_history)
    },
    urlParams() {
      return this.query_history_spot + this.str_query_history
    },
    history_spots_complete() {
      const query_board = this.$store.getters[SNK('query_board', this.namespace)]
      const history_spots = this.$store.getters[SNK('history_spots', this.namespace)]

      const firstSpot = history_spots[0]

      const mockEliteFlop =
        this.solElite &&
        this.solElite.want_use &&
        !this.solElite.is_preflop &&
        !this.solElite.loading_what &&
        (!this.solElite.must_create || this.solElite.must_add_board)

      let res = []
      if (mockEliteFlop && !history_spots.length) {
        res = [
          {
            id: 'flop',
            street: 'flop',
            type: 'board',
            placeholder: true,
          },
        ]
      } else {
        res = getCompleteHistorySpots({
          history_spots,
          board: query_board,
          hasFirstBoardSpot: firstSpot?.street === 'flop' || mockEliteFlop,
        })
      }

      const { hasFilter } = this
      const canAiPrefill = this.hasFlopPrefillBtn
      res = res.map((i, idx) => {
        let r = {
          ...i,
          position: i.position === 'UTG+1' ? 'UTG1' : i.position === 'UTG+2' ? 'UTG2' : i.position,
          active: !this.loader && this.hasActiveSpot && i.is_active,

          // In trainer we have to prevent user in custom spot mode to start from first node
          // In trainer we have to hide replay btns on first node (handled in confirmBtnsProps)
          // In Study solutions we display message but skip to second node (handled in gamestate_set_api_history)
          // get mocks at
          // https://app.gtowizard.com/solutions?custree_id=d3329852-0877-4e14-bd24-c720cc77a082&cussol_id=72dce6c1-8ad1-4fc4-b9d4-4d9b86a30256&solution_type=gwiz&gametype=SpinsGeneralV2&depth=33&stacks=33-33-33&history_spot=0&tmpId=1698833591904&soltab=range&gmfft_sort_key=0&gmfft_sort_order=desc
        }

        // for custom solutions reports
        if (this.useTreePreviewReports && i.type === 'player' && this.ggStreet === i.street) {
          r = { ...r, type: 'showdown' }
        }

        if (i.type === 'board') {
          if (this.ggStreet === i.street) {
            r = null
          }
          if (hasFilter) {
            r = { ...r, is_filter: true }
          }
          if (canAiPrefill) {
            const prevSpotAction = getSpotSelectedAction({ spot: res[idx - 1] }) || {}
            r = {
              ...r,
              has_ai_prefill: prevSpotAction.can_ai_solve,
            }
          }
        }

        if (i.type === 'showdown' && this.hasFlopSolutionEndNode && i.street === 'preflop') {
          const prevSpotAction = getSpotSelectedAction({ spot: res[idx - 1] }) || {}
          r = {
            ...r,
            type: prevSpotAction.is_fe_solution_end ? 'solution_end' : 'showdown',
            disabled: prevSpotAction.is_fe_solution_end
              ? !this.clickableFlopSolutionEndNode
              : !this.clickableGG,
            has_ai_prefill: canAiPrefill && prevSpotAction.can_ai_solve,
          }
        }
        if (
          !this.mobile &&
          this.hasCorrectness &&
          i.type === 'player' &&
          !!i.selected_action_code
        ) {
          r = {
            ...r,
            hasCorrectnessTooltip: true,
            hasCorrectnessIcon: true,
          }
        }
        return r
      })

      if (this.loader?.skeleton) {
        if (res.length) {
          // console.log('1')
          res = res.map(i => ({
            ...i,
            isSkeleton: this.showBoardWhenLoading ? i.type === 'player' : true,
            disabled: true,
          }))
        } else {
          // console.log('2')
          const tiles = this.skeletonTiles || this.getTilesByPlayers()
          res = [...new Array(tiles).keys()].map((i, idx) => ({ id: idx, isSkeleton: true }))
        }
      }

      return res.filter(Boolean)
    },
    history_spots() {
      const res = this.$store.getters[SNK('history_spots', this.namespace)]
      return res
    },
    loader() {
      let res = {
        extra: false,
        history_spot: -1,
        dots: false,
        spinner: false,
        skeleton: false,
      }

      // this block is just for trainer
      if (this.customLoadingEvents && !this.loadingSkeleton) {
        const anim = this.player_animation
        const history_spot = (anim?.history_spot ?? '').toString()
        const hero_loading = anim?.waiting && !anim?.is_bot
        const bot_loading = anim?.waiting && anim?.is_bot
        const practice_showdown = this.$store.getters.api_get(
          'api_practice_showdown',
          this.namespace,
        )?.loading
        res = {
          ...res,
          extra: anim.extra_spot || practice_showdown,
          spinner: hero_loading || practice_showdown,
          history_spot: Number(history_spot),
          dots: bot_loading,
        }
        res = res.spinner || res.dots ? res : null
        return res
      }

      if (this.useTreePreview || this.useTreePreviewReports) {
        const treePreview = this.$store.getters.api_get('api_poker_tree_preview', this.namespace)
        const custree = this.$store.getters.api_get(
          'api_poker_custom_solution_tree',
          this.namespace,
        )

        const treePreviewSpinner =
          custree?.loading || (treePreview?.loading && treePreview?.method === 'post')

        res = { ...res, extra: true, spinner: treePreviewSpinner }
        res = res.spinner ? res : null

        return res
      }

      const history = this.$store.getters.api_get('api_poker_history', this.namespace)
      const next_actions = this.$store.getters.api_get('api_poker_next_actions', this.namespace)
      const cussol = this.$store.getters.api_get('api_poker_custom_solution', this.namespace)

      const cussolSkeleton = cussol?.loading && cussol?.method === 'get' && !next_actions?.loading
      const cussolSpinner = !cussolSkeleton && cussol?.loading && cussol?.method === 'post'

      const skeleton = cussolSkeleton || history?.loading || this.loadingSkeleton
      const spinner = !this.loadingSkeleton && (cussolSpinner || next_actions?.loading)

      res = {
        ...res,
        extra: true,
        skeleton,
        spinner,
        ...(this.customLoading || {}),
      }

      res = res.spinner || res.skeleton ? res : null
      return res
    },
    board() {
      const query_board = this.$store.getters[SNK('query_board', this.namespace)]
      const res = {
        flop: getCardsByStreet(query_board, 'flop'),
        turn: getCardsByStreet(query_board, 'turn'),
        river: getCardsByStreet(query_board, 'river'),
      }
      return res
    },
    board_tmp() {
      const street_board_any =
        this.$store.getters[SNK('street_board_any', this.namespace)]('river').join('')
      const res = {
        flop: getCardsByStreet(street_board_any, 'flop') || 'EeEeEe',
        turn: getCardsByStreet(street_board_any, 'turn') || 'Ee',
        river: getCardsByStreet(street_board_any, 'river') || 'Ee',
      }
      return res
    },
    visibleBoard() {
      const res = {
        flop: this.hasEmptyBoard
          ? 'EeEeEe'
          : this.hasTmpBoard
            ? this.board_tmp.flop
            : this.board.flop || 'EeEeEe',
        turn: this.hasEmptyBoard
          ? 'Ee'
          : this.hasTmpBoard
            ? this.board_tmp.turn
            : this.board.turn || 'Ee',
        river: this.hasEmptyBoard
          ? 'Ee'
          : this.hasTmpBoard
            ? this.board_tmp.river
            : this.board.river || 'Ee',
      }
      return res
    },
    filterParams() {
      const query_board_filter = this.$store.getters[SNK('query_board_filter', this.namespace)]

      const res = { ...query_board_filter }
      Object.keys(query_board_filter).forEach(key => {
        res[key] = res[key].split(',').filter(i => i)
      })
      return res
    },
    gameformat() {
      return this.$store.getters[SNK('gameformat', this.namespace)]
    },
    globals_action() {
      const { globals_action } = this.$store.getters
      return globals_action
    },
    player_animation() {
      return this.$store.getters[SNK('player_animation', this.namespace)] || {}
    },
    street() {
      return this.$store.getters.street
    },
    showBetsIn() {
      return this.$store.getters.show_bets_in
    },
    showEvIn() {
      return this.$store.getters.show_ev_in
    },
    mobile() {
      return this.$store.getters.globals_mobile
    },
    scrollerWatchActionId() {
      return `horizontal_scroll_check${this.namespace ? `_${this.namespace}` : ''}`
    },
  },
  watch: {
    urlParams() {
      this.handleUrlParamsChange()
    },
    history_spots() {
      nextTick(() => {
        if (this.$refs.HspotsContainer?.$refs?.HorizolnalScroller) {
          this.$refs.HspotsContainer.$refs.HorizolnalScroller.checkOverflow()
        }
      })
    },
    globals_action(a) {
      if (!a) return
      if (a === this.scrollerWatchActionId) {
        const activeSpot = this.history_spots_complete.find(e => e.active)
        const eventCard = this.$refs[`event_${activeSpot?.id}`]
        if (eventCard?.[0]) {
          nextTick(() => {
            eventCard[0].scrollToMe()
          })
        }
        // actions is emptied on the scroller
        return
      }
      if (a === 'disable_history_watcher') {
        if (this.hasWatcherEnabled) {
          this.$store.dispatch('globals_action', '')
          this.watcherEnabled = false
        }
        return
      }
      if (a === 'enable_history_watcher') {
        if (this.hasWatcherEnabled) {
          this.$store.dispatch('globals_action', '')
          this.watcherEnabled = true
        }
        return
      }
      if (a.includes('playerjump_')) {
        this.$store.dispatch('globals_action', '')
        const position = a.replace('playerjump_', '')
        const { history_spots } = this

        let item = history_spots.find(e => e.position === position)
        item = JSON.parse(JSON.stringify(history_spots))
          .reverse()
          .find(e => e.position === position)
        if (item) {
          this.handleClick(item)
        }
      }
      if (a.includes('hsjump_')) {
        this.$store.dispatch('globals_action', '')
        const spotId = a.replace('hsjump_', '')
        const item = this.history_spots_complete.find(e => e.id.toString() === spotId)
        if (item) {
          this.handleClick(item)
        }
      }
    },
  },
}
</script>

<style lang="scss">
.dialog-flop .flop {
  .hspot-card_inner {
    .flop {
      box-shadow: 0 0 0 15px var(--clr-primary) !important;
    }
  }
}

.history-events-pencil-icon {
  position: absolute !important;
  bottom: 0.25rem;
  right: 0.25rem;
}
.empty-card-icon {
  position: absolute !important;
  top: 0.8rem;
  right: 0.5rem;
  color: var(--clr-grey-2);
}
.history-events-pencil-icon-relative {
  position: relative !important;
  top: -2px;
  margin-left: 1rem;
  height: 0 !important;
}
@media (max-width: 1023px) {
  .empty-card-icon {
    display: none;
  }
}

.skeleton-card {
  height: 144px;
  flex-shrink: 0;
  min-width: 118px;
  border-radius: 4px;
  margin: 2px;
  animation: opacityPulse 2s infinite;
  background: var(--clr-white-0);
  opacity: 0.05;
}

@keyframes opacityPulse {
  0% {
    opacity: 0.05;
  }
  50% {
    opacity: 0.075;
  }
  100% {
    opacity: 0.05;
  }
}
</style>
